import { useCallback, useEffect, useState, type ReactNode } from 'react'
import type { Status, TaskRow } from '../lib/api'
import { bucketTasks, shopDate } from '../lib/grouping'
import { listHeadlines, type Headline } from '../lib/headlinesApi'
import { listIssues, type Issue } from '../lib/issuesApi'
import { listKpiMetrics, type KpiMetric } from '../lib/kpiApi'
import TaskCard from './TaskCard'
import KpiCard from './KpiCard'

interface Props {
  token: string
  /** Already loaded by App — get_my_tasks is already scoped to the caller. */
  tasks: TaskRow[]
  busy: Set<string>
  onStatus: (assignmentId: string, status: Status) => void
  onNote: (assignmentId: string, note: string) => void
  onTasksChanged: () => void
  /** Jump to the tab that owns full editing for a headline or an issue. */
  onOpenTab: (view: 'headlines' | 'issues') => void
  /** Admins may edit a task's shared details from here; nobody else can (0021). */
  isAdmin: boolean
  /** "Create an issue" from one of your to-dos — App opens the linked issue form. */
  onCreateIssue: (task: TaskRow) => void
}

interface SectionProps {
  id: string
  label: string
  count: number
  /** e.g. 'section-toggle--overdue' for the urgency colour on a task bucket. */
  toneClass?: string
  collapsed: boolean
  onToggle: (id: string) => void
  children: ReactNode
}

/** One collapsible block: a tappable header (caret + label + count) and its content. */
function Section({ id, label, count, toneClass, collapsed, onToggle, children }: SectionProps) {
  return (
    <section className="group">
      <button
        type="button"
        className={'section-toggle' + (toneClass ? ` ${toneClass}` : '')}
        aria-expanded={!collapsed}
        onClick={() => onToggle(id)}
      >
        <span className="fold__caret" aria-hidden="true">
          {collapsed ? '▸' : '▾'}
        </span>
        {label}
        <span className="group__count">{count}</span>
      </button>
      {!collapsed && children}
    </section>
  )
}

/**
 * "My Portal" — everything tied to the signed-in person, gathered onto one
 * screen instead of hunting across four tabs for it.
 *
 * Deliberately a SUMMARY, not a second copy of each tab's full editor. Tasks
 * and your own KPI numbers get their real interactive cards, because ticking a
 * status or entering a number is exactly the fast, repeated action this page
 * exists to speed up. Headlines and issues get a read-only line plus a jump
 * button, because reclassifying an issue or archiving a headline is a
 * management action that belongs in its own tab, not duplicated here — this
 * page's job is to answer "what's mine and how does it stand," not to be a
 * second Issues tab.
 *
 * Every section starts open — the point of the page is seeing it all at a
 * glance — and collapsing one is just a way to tidy the screen once you've
 * checked it, not a saved preference: it resets to fully open next visit.
 */
export default function MyPortal({
  token,
  tasks,
  busy,
  onStatus,
  onNote,
  onTasksChanged,
  onOpenTab,
  isAdmin,
  onCreateIssue,
}: Props) {
  const [headlines, setHeadlines] = useState<Headline[] | null>(null)
  const [issues, setIssues] = useState<Issue[] | null>(null)
  const [metrics, setMetrics] = useState<KpiMetric[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  const toggleSection = (id: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const load = useCallback(async () => {
    try {
      const [h, i, k] = await Promise.all([
        listHeadlines(token),
        listIssues(token),
        listKpiMetrics(token),
      ])
      setHeadlines(h)
      setIssues(i)
      setMetrics(k)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  if (headlines === null || issues === null || metrics === null) {
    return <p className="muted">Loading…</p>
  }

  const myHeadlines = headlines.filter((h) => h.mine)
  const myIssues = issues.filter((i) => i.mine)
  const myMetrics = metrics.filter((m) => m.mine)
  const buckets = bucketTasks(tasks)

  const nothingAtAll =
    tasks.length === 0 &&
    myHeadlines.length === 0 &&
    myIssues.length === 0 &&
    myMetrics.length === 0

  return (
    <section>
      {problem && <p className="banner">{problem}</p>}

      {nothingAtAll && (
        <div className="panel">
          <h2 className="panel__title">Nothing here yet</h2>
          <p className="muted">
            Tasks assigned to you, your KPI numbers, and anything you've reported or
            shared will show up here.
          </p>
        </div>
      )}

      {myMetrics.length > 0 && (
        <Section
          id="numbers"
          label="Your numbers"
          count={myMetrics.length}
          collapsed={collapsedSections.has('numbers')}
          onToggle={toggleSection}
        >
          {myMetrics.map((m) => (
            <KpiCard
              key={m.metric_id}
              metric={m}
              token={token}
              // Forced false, deliberately: this page is for entering your own
              // number, not for the metric-management controls (Edit/Archive/
              // correcting someone else's) — those stay on the Scorecard tab
              // even for an admin looking at their own metric here.
              isAdmin={false}
              onChanged={() => void load()}
              onEdit={() => {}}
              onArchiveToggle={() => {}}
              archiveBusy={false}
            />
          ))}
        </Section>
      )}

      {tasks.length > 0 &&
        buckets.map((bucket) => (
          <Section
            key={bucket.key}
            id={bucket.key}
            // "Later" reads oddly as a lone section on a personal summary —
            // everywhere else (the Tasks tab) it sits alongside the other
            // urgency buckets and makes sense there, so this override is
            // local to My Portal rather than a change to bucketTasks() itself.
            label={bucket.key === 'later' ? 'Tasks' : bucket.label}
            count={bucket.tasks.length}
            toneClass={`section-toggle--${bucket.key}`}
            collapsed={collapsedSections.has(bucket.key)}
            onToggle={toggleSection}
          >
            {bucket.tasks.map((task) => (
              <TaskCard
                key={task.assignment_id}
                task={task}
                token={token}
                busy={busy.has(task.assignment_id)}
                onStatus={onStatus}
                onNote={onNote}
                onLinksChanged={onTasksChanged}
                canEditDetails={isAdmin}
                onCreateIssue={onCreateIssue}
              />
            ))}
          </Section>
        ))}

      {myIssues.length > 0 && (
        <Section
          id="issues"
          label="Your issues"
          count={myIssues.length}
          collapsed={collapsedSections.has('issues')}
          onToggle={toggleSection}
        >
          {myIssues.map((issue) => (
            <article key={issue.id} className="card">
              <div className="card__head">
                <h3 className="card__title">{issue.title}</h3>
                <span className="card__badges">
                  {issue.archived && <span className="chip chip--low">Archived</span>}
                  {issue.urgency !== 'Medium' && (
                    <span className={`chip chip--${issue.urgency.toLowerCase()}`}>
                      {issue.urgency}
                    </span>
                  )}
                </span>
              </div>
              <p className="card__due">
                <span className={'chip ' + (issue.status === 'Solved' ? 'chip--ok' : 'chip--low')}>
                  {issue.status}
                </span>
                {' · '}
                Created {shopDate(issue.created_at)}
                {issue.task_count > 0
                  ? ` · ${issue.task_count} ${issue.task_count === 1 ? 'task' : 'tasks'}`
                  : ''}
              </p>
              {issue.resolution_notes && (
                <p className="card__brief">{issue.resolution_notes}</p>
              )}
              <button type="button" className="linkish" onClick={() => onOpenTab('issues')}>
                View in Issues →
              </button>
            </article>
          ))}
        </Section>
      )}

      {myHeadlines.length > 0 && (
        <Section
          id="headlines"
          label="Your headlines"
          count={myHeadlines.length}
          collapsed={collapsedSections.has('headlines')}
          onToggle={toggleSection}
        >
          {myHeadlines.map((h) => (
            <article key={h.id} className="card">
              <div className="card__head">
                <h3 className="card__title">{h.body}</h3>
                <span className={'chip ' + (h.shared ? 'chip--ok' : '')}>
                  {h.shared ? 'Shared' : 'To share'}
                </span>
              </div>
              <p className="card__due">
                Created {shopDate(h.created_at)}
                {h.issue_title ? ` · became the issue "${h.issue_title}"` : ''}
              </p>
              <button type="button" className="linkish" onClick={() => onOpenTab('headlines')}>
                View in Headlines →
              </button>
            </article>
          ))}
        </Section>
      )}
    </section>
  )
}
